import { useState } from "react";
import { KeyRound } from "lucide-react";
import { useAuth } from "../../auth/context";
import { useAppServices } from "../../hook/services";
import Button from "../ui/Button";
import Input, { Label } from "../ui/Input";
import Modal from "../ui/Modal";
import { useToast } from "../ui/Toast";

const ROLE_LABEL = { admin: "Admin", member: "Member", viewer: "Viewer" };

/**
 * The signed-in member's own account: who they are and a password change.
 *
 * `required` is for a temporary password — the dialog can't be closed until a
 * new one is set (the member can still sign out).
 */
const AccountDialog = ({ onClose, required = false }) => {
  const { user, role, replaceSession, logout } = useAuth();
  const { team } = useAppServices();
  const toast = useToast();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 10) return setError("Use at least 10 characters for the new password.");
    if (next !== confirm) return setError("The two new passwords don't match.");

    setSaving(true);
    const { response, error: err } = await team.ChangePassword({ currentPassword: current, newPassword: next });
    setSaving(false);
    if (err || !response?.token) return setError(err?.message || err?.error || "Could not change your password.");

    replaceSession(response.token, { mustChangePassword: false });
    toast("Password changed", "success");
    onClose?.();
  };

  return (
    <Modal
      title={required ? "Set a new password" : "Your account"}
      description={
        required
          ? "You signed in with a temporary password. Choose your own to continue."
          : `${user?.username || ""} · ${user?.email || ""}${role ? ` · ${ROLE_LABEL[role]}` : ""}`
      }
      icon={<KeyRound size={18} />}
      onClose={required ? undefined : onClose}
      footer={
        <>
          {required ? (
            <Button type="button" variant="ghost" onClick={logout}>
              Sign out
            </Button>
          ) : (
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button type="submit" form="account-password" variant="gradient" loading={saving} disabled={saving}>
            Change password
          </Button>
        </>
      }
    >
      <form id="account-password" onSubmit={submit} className="space-y-3.5">
        <div className="space-y-1.5">
          <Label htmlFor="pw-current">{required ? "Temporary password" : "Current password"}</Label>
          <Input id="pw-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pw-next" hint="At least 10 characters">
            New password
          </Label>
          <Input id="pw-next" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pw-confirm">Repeat new password</Label>
          <Input id="pw-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
        </div>
        {error && <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{error}</p>}
        {!required && <p className="text-[12px] text-ink-400">Changing it signs you out everywhere else.</p>}
      </form>
    </Modal>
  );
};

export default AccountDialog;
